//project imports
import {
	siteDetailPath,
	siteAssetPath,
	siteAssetRegisterPath,
	siteDepartmentPath,
	modelDetail,
	modelStages,
	modelZones,
	modelIntervals,
	modelRoles,
	modelQuestions,
	modelTask,
	modelServiceLayout,
	modelAssest,
	analysisPath,
	defectsPath,
	feedbackPath,
	modelsPath,
	servicesPath,
	usersPath,
} from "helpers/routePaths";
import {
	APPLICATION,
	SITE_APP as SiteApp,
	HISTORY_CAPTIONS as historyCaptions,
} from "./constants-history";

// Lazy accessor for the i18n singleton. constants.js is a foundational module in
// the i18n -> services/localisation -> services/api -> constants dependency chain,
// so importing i18n at module top-level creates a circular import (BASE_API_PATH
// temporal-dead-zone error). require() defers the load until a builder runs at
// render time, by which point every module is initialised.
const getI18n = () => require("i18n").default;

//project constants
export const BASE_API_PATH = "/api/";

export const RICH_TEXT_FIELDS = [
	"Note",
	"Notes",
	"Description",
	"Comments",
	"ActionTaken",
];

export const LICENSETYPE = {
	get totalUsers() {
		return getI18n().t("label.totalUsers");
	},
	get concurrentUsers() {
		return getI18n().t("label.concurrentUsers");
	},
	get perRoleWorkOrder() {
		return getI18n().t("label.perRoleWorkOrder");
	},
	get siteBasedLicensing() {
		return getI18n().t("label.siteBasedLicensing");
	},
	get applicationBasedLicensing() {
		return getI18n().t("label.applicationBasedLicensing");
	},
};

export const clientOptions = () => [
	{ label: LICENSETYPE.totalUsers, value: 1 }, //0
	{ label: LICENSETYPE.concurrentUsers, value: 2 }, //1
	{ label: LICENSETYPE.perRoleWorkOrder, value: 3 }, //2
	{ label: LICENSETYPE.siteBasedLicensing, value: 4 }, //3
];

export const shareModelsOptions = () => [
	{ label: getI18n().t("option.noSharing"), value: 0 }, //0
	{ label: getI18n().t("option.clientSiteToSite"), value: 1 }, //1
	{ label: getI18n().t("option.clientToClientSite"), value: 2 }, //2
];

export const canAddSiteApplications = () => [
	{ label: getI18n().t("option.allowed"), value: true }, //true
	{ label: getI18n().t("option.notAllowed"), value: false }, // false
];

export const siteSettingShareModelsOptions = () => [
	{
		label: getI18n().t("message.shareModelsToOwnSites"),
		value: 1,
	}, //1
	{
		label: getI18n().t("message.shareModelsToOtherClients"),
		value: 2,
	}, //2
];
export const siteOptions = () => [
	{ label: LICENSETYPE.totalUsers, value: 1 }, //0
	{ label: LICENSETYPE.concurrentUsers, value: 2 }, //1
	{ label: LICENSETYPE.perRoleWorkOrder, value: 3 }, //2
	{ label: LICENSETYPE.applicationBasedLicensing, value: 5 }, //3
];

export const siteApplicationOptions = () => [
	{ label: LICENSETYPE.totalUsers, value: 1 }, //0
	{ label: LICENSETYPE.concurrentUsers, value: 2 }, //1
	{ label: LICENSETYPE.perRoleWorkOrder, value: 3 }, //2
];

export const HistoryInfo = {
	1: {
		icon: "historyadd",
		get type() {
			return getI18n().t("label.historyAdded");
		},
	},
	2: {
		icon: "historyupdate",
		get type() {
			return getI18n().t("label.historyEdited");
		},
	},
	3: {
		icon: "historydelete",
		get type() {
			return getI18n().t("label.historyDeleted");
		},
	},
	4: {
		icon: "historyread",
		get type() {
			return getI18n().t("label.historyRead");
		},
	},
	5: {
		icon: "historyadd",
		get type() {
			return getI18n().t("label.historyRestored");
		},
	},
};

export const SubTableModelTask = (customCaptions) => ({
	76: customCaptions["stage"],
	77: customCaptions["zone"],
	78: customCaptions["role"],
	79: customCaptions["interval"],
	80: customCaptions["part"],
	81: customCaptions["tool"],
	82: customCaptions["permit"],
	83: getI18n().t("history.image"),
	84: getI18n().t("history.attachment"),
	85: customCaptions["question"],
});

export const HistoryEntityCaptions = (customCaptions) => ({
	0: getI18n().t("history.application"),
	1: getI18n().t("history.modelStatus"),
	2: getI18n().t("history.pauseReason"),
	3: getI18n().t("history.pauseSubcategory"),
	4: getI18n().t("history.position"),
	5: getI18n().t("history.action"),
	6: getI18n().t("history.modelType"),
	7: getI18n().t("history.system"),
	8: getI18n().t("history.operatingMode"),
	9: getI18n().t("history.role"),
	10: getI18n().t("history.defectType"),
	11: getI18n().t("history.defectStatus"),
	12: getI18n().t("history.defectRiskRating"),
	13: getI18n().t("history.feedbackStatus"),
	14: getI18n().t("history.feedbackPriority"),
	15: getI18n().t("history.feedbackClassification"),
	16: getI18n().t("history.missingPartToolReason"),
	17: getI18n().t("history.skipTaskReason"),
	18: getI18n().t("history.stopReason"),
	19: getI18n().t("history.changeStatusReason"),
	20: getI18n().t("history.user"),
	21: getI18n().t("history.client"),
	22: getI18n().t("history.clientNote"),
	23: getI18n().t("history.clientApplication"),
	24: getI18n().t("history.clientDocument"),
	25: getI18n().t("history.region"),
	26: getI18n().t("history.site"),
	28: getI18n().t("history.siteEntity", { entity: customCaptions?.["department"] || "Department" }),
	29: getI18n().t("history.siteEntity", { entity: customCaptions?.["asset"] || "Asset" }),
	30: getI18n().t("history.siteApplication"),
	31: customCaptions?.["pauseReason"],
	32: getI18n().t("label.entitySubcategory", { entity: customCaptions?.["pauseReason"] }),
	33: getI18n().t("label.missingPartToolReason", { part: customCaptions?.["part"], tool: customCaptions?.["tool"] }),
	34: `${customCaptions?.["assetReference"]}`,
	35: getI18n().t("history.report"),
	36: customCaptions?.["skipReason"],
	37: customCaptions?.["stopReason"],
	38: getI18n().t("history.changeStatusReason"),
	39: getI18n().t("label.entityStatus", { entity: customCaptions?.["model"] }),
	40: getI18n().t("label.entityType", { entity: customCaptions?.["model"] }),
	41: customCaptions?.["actionRequired"],
	42: customCaptions?.["system"],
	43: customCaptions?.["operatingMode"],
	45: customCaptions?.["role"],
	46: customCaptions?.["position"],
	47: `${customCaptions?.["defect"]} ${customCaptions?.["riskRating"]}`,
	48: customCaptions?.["defectStatus"],
	49: customCaptions?.["defectType"],
	50: `${customCaptions?.["feedback"]} ${customCaptions?.["classification"]}`,
	51: `${customCaptions?.["feedback"]} ${customCaptions?.["priority"]}`,
	52: customCaptions?.["feedbackStatus"],
	53: getI18n().t("history.siteApplicationKeyContact"),
	54: getI18n().t("history.clientUser"),
	55: getI18n().t("history.clientUserSite"),
	56: customCaptions?.["user"],
	57: getI18n().t("history.clientUserNote"),
	58: `${customCaptions?.["user"]} ${customCaptions?.["service"]} ${customCaptions?.["department"]}`,
	59: `${customCaptions?.["user"]} ${customCaptions?.["service"]} ${customCaptions?.["role"]}`,
	60: `${customCaptions?.["model"]}`,
	61: getI18n().t("label.entityVersion", { entity: customCaptions?.["model"] }),
	62: getI18n().t("label.entityDocument", { entity: customCaptions?.["model"] }),
	63: getI18n().t("label.entityNote", { entity: customCaptions?.["model"] }),
	64: customCaptions?.["asset"],
	65: customCaptions?.["stage"],
	66: customCaptions?.["zone"],
	67: customCaptions?.["interval"],
	68: customCaptions?.["role"],
	69: customCaptions?.["question"],
	70: customCaptions?.["task"],
	71: customCaptions?.["taskListNo"],
	72: getI18n().t("history.modelVersionQuestionRole", { model: customCaptions?.["model"], question: customCaptions?.["question"], role: customCaptions?.["role"] }),
	73: getI18n().t("history.modelVersionIntervalInclude", { model: customCaptions?.["model"], interval: customCaptions?.["interval"] }),
	74: getI18n().t("history.modelVersionQuestionOption", { model: customCaptions?.["model"], question: customCaptions?.["question"] }),
	75: getI18n().t("history.modelTaskNote", { model: customCaptions?.["model"], task: customCaptions?.["task"] }),
	76: `${customCaptions?.["task"]} ${customCaptions?.["stage"]}`,
	77: `${customCaptions?.["task"]} ${customCaptions?.["zone"]}`,
	78: `${customCaptions?.["task"]} ${customCaptions?.["role"]}`,
	79: `${customCaptions?.["task"]} ${customCaptions?.["interval"]}`,
	80: `${customCaptions?.["task"]} ${customCaptions?.["part"]}`,
	81: `${customCaptions?.["task"]} ${customCaptions?.["tool"]}`,
	82: `${customCaptions?.["task"]} ${customCaptions?.["permit"]}`,
	83: getI18n().t("label.entityImage", { entity: customCaptions?.["task"] }),
	84: getI18n().t("label.entityAttachment", { entity: customCaptions?.["task"] }),
	85: `${customCaptions?.["task"]} ${customCaptions?.["question"]}`,
	86: getI18n().t("history.modelVersionTaskQuestionOption", { model: customCaptions?.["model"], task: customCaptions?.["task"], question: customCaptions?.["question"] }),
	87: getI18n().t("label.entityImport", { entity: customCaptions?.["model"] }),
	88: getI18n().t("history.modelImportAction", { model: customCaptions?.["model"] }),
	89: getI18n().t("history.modelImportEntity", { model: customCaptions?.["model"], entity: customCaptions?.["lubricant"] }),
	90: getI18n().t("history.modelImportEntity", { model: customCaptions?.["model"], entity: customCaptions?.["operatingMode"] }),
	91: getI18n().t("history.modelImportEntity", { model: customCaptions?.["model"], entity: customCaptions?.["system"] }),
	92: getI18n().t("history.modelImportEntity", { model: customCaptions?.["model"], entity: customCaptions?.["role"] }),
	93: getI18n().t("history.modelDepartment", { model: customCaptions?.["model"], department: customCaptions?.["department"] }),
	94: customCaptions?.["service"],
	95: `${customCaptions?.["user"]} ${customCaptions?.["service"]} ${customCaptions?.["model"]}`,
	96: ` ${customCaptions?.["service"]} ${customCaptions?.["task"]}`,
	97: getI18n().t("label.entitySession", { entity: customCaptions?.["service"] }),
	98: ` ${customCaptions?.["service"]} ${customCaptions?.["task"]} ${customCaptions?.["question"]}`,
	99: getI18n().t("history.serviceTaskQuestionOption", { service: customCaptions?.["service"], task: customCaptions?.["task"], question: customCaptions?.["question"] }),
	100: getI18n().t("history.serviceSessionQuestion", { service: customCaptions?.["service"], question: customCaptions?.["question"] }),
	101: getI18n().t("history.serviceSessionQuestionOption", { service: customCaptions?.["service"], question: customCaptions?.["question"] }),
	102: getI18n().t("label.entityNote", { entity: customCaptions?.["service"] }),
	103: getI18n().t("history.serviceMissingPartTool", { service: customCaptions?.["service"], part: customCaptions?.["part"], tool: customCaptions?.["tool"] }),
	104: `${customCaptions?.["service"]} ${customCaptions?.["pause"]}`,
	105: getI18n().t("label.entityStatusChange", { entity: customCaptions?.["service"] }),
	106: customCaptions?.["noticeboard"] || "Noticeboard",
	107: customCaptions?.["feedback"],
	108: getI18n().t("label.entityNote", { entity: customCaptions?.["feedback"] }),
	109: getI18n().t("label.entityImage", { entity: customCaptions?.["feedback"] }),
	110: customCaptions?.["defect"],
	111: getI18n().t("label.entityNote", { entity: customCaptions?.["defect"] }),
	112: getI18n().t("label.entityImage", { entity: customCaptions?.["defect"] }),
	113: `${customCaptions?.["defect"]} ${customCaptions?.["part"]}`,
	114: getI18n().t("history.modelVersionChild", { model: customCaptions?.["model"], child: customCaptions?.["department"] }),
	115: getI18n().t("history.noticeboardDepartment", { department: customCaptions?.["department"] }),
	116: getI18n().t("history.modelVersionChild", { model: customCaptions?.["model"], child: customCaptions?.["arrangement"] }),
	117: getI18n().t("history.modelVersionTaskArrangement", { model: customCaptions?.["model"], task: customCaptions?.["task"], arrangement: customCaptions?.["arrangement"] }),
	118: getI18n().t("history.mobileError"),
	130: getI18n().t("label.entityDocument", { entity: customCaptions?.["feedback"] }),
});

export const HistoryCaptions = {
	services: "services",
	modelVersionQuestions: "modelVersionQuestions",
	defects: "defects",
	clientUserSite: "clientUserSite",
	feedback: "feedback",
	noticeBoard: "noticeBoard",
	modelVersionTasks: "modelVersionTasks",
	modelVersionRoles: "modelVersionRoles",
	modelVersionIntervals: "modelVersionIntervals",
	modelVersionZones: "modelVersionZones",
	modelVersionStages: "modelVersionStages",
	modelVersionArrangements: "modelVersionArrangements",
	modelAssets: "modelAssets",
	modelVersions: "modelVersions",
	...historyCaptions,
};

export const HistoryCustomCaption = (from, customCaptions, showParts) => {
	switch (from) {
		case HistoryCaptions.modelVersions:
			return {
				DevModelVersionID: getI18n().t("label.developmentEntityVersion", {
				entity: customCaptions?.["model"],
			}),
				ModelID: `${customCaptions?.["model"]}`,
				Version: getI18n().t("history.version"),
				ModelStatusID: getI18n().t("label.entityStatus", { entity: customCaptions?.["model"] }),
				Name: customCaptions?.["Make"],
				Model: customCaptions?.["model"],
				ModelName: getI18n().t("label.entityName", { entity: customCaptions?.["model"] }),
				ModelTypeID: `${customCaptions?.["modelType"]}`,
				SerialNumberRange: `${customCaptions?.["serialNumberRange"]}`,
				ImageKey: getI18n().t("history.imageKey"),
				ThumbnailKey: getI18n().t("history.thumbnailImageKey"),
				ShowTimer: getI18n().t("history.showTimer"),
				AllowTickAllPartsTools: `All Tick All for ${showParts ? `${customCaptions?.["partPlural"]} and` : ""
					} ${customCaptions?.["toolPlural"]} `,
				ListPartsToolsByStageZone: `List ${showParts ? `${customCaptions?.["partPlural"]} /` : ""
					} ${customCaptions?.["toolPlural"]} by ${customCaptions?.["stage"]} / ${customCaptions?.["zone"]
					}`,
				HideParts: getI18n().t("history.hideEntity", { entity: customCaptions?.["partPlural"] }),
				HideTools: getI18n().t("history.hideEntity", { entity: customCaptions?.["toolPlural"] }),
				IsPublished: getI18n().t("history.published"),
				EnableIntervalAutoInclude: getI18n().t("history.enableEntityAutoInclude", { entity: customCaptions?.["interval"] }),
				ReviewDate: getI18n().t("history.reviewDate"),
				ImageURL: getI18n().t("history.imageURL"),
				ThumbnailURL: getI18n().t("history.thumbnailImageURL"),
				ActiveModelVersionID: getI18n().t("label.activeEntityVersion", {
				entity: customCaptions?.["model"],
			}),
				SiteAssetID: `${customCaptions?.["asset"]} `,
				SiteID: getI18n().t("history.siteId"),
				SiteAssetReferenceID: `${customCaptions?.["assetReference"]}`,
			};
		case HistoryCaptions.modelDocument:
			return {
				ModelID: `${customCaptions?.["model"]}`,
				DocumentKey: getI18n().t("history.documentKey"),
				DocumentURL: getI18n().t("history.documentURL"),
				Filename: getI18n().t("history.fileName"),
				Date: getI18n().t("history.date"),
				Name: getI18n().t("history.name"),
			};

		case HistoryCaptions.modelNote:
			return {
				ModelID: `${customCaptions?.["model"]}`,
				Note: getI18n().t("history.note2"),
				Date: getI18n().t("history.date"),
				Name: getI18n().t("history.name"),
			};

		case HistoryCaptions.modelVersionStage:
			return {
				ModelVersionID: getI18n().t("label.entityVersion", { entity: customCaptions?.["model"] }),
				Name: getI18n().t("history.name"),
				ImageKey: getI18n().t("history.imageKey"),
				ThumbnailKey: getI18n().t("history.thumbnailKey"),
				HasZones: getI18n().t("history.hasEntity", { entity: customCaptions?.["zone"] }),
			};

		case HistoryCaptions.modelVersionZone:
			return {
				ModelVersionID: getI18n().t("label.entityVersion", { entity: customCaptions?.["model"] }),
				Name: getI18n().t("history.name"),
				ImageKey: getI18n().t("history.imageKey"),
				ThumbnailKey: getI18n().t("history.thumbnailKey"),
			};

		case HistoryCaptions.modelVersionInterval:
			return {
				ModelVersionID: getI18n().t("label.entityVersion", { entity: customCaptions?.["model"] }),
				Name: getI18n().t("history.name"),
			};

		case HistoryCaptions.modelVersionRole:
			return {
				ModelVersionID: getI18n().t("label.entityVersion", { entity: customCaptions?.["model"] }),
				Name: getI18n().t("history.name2"),
				roleID: getI18n().t("history.role2"),
				siteDepartmentID: getI18n().t("history.siteEntity", { entity: customCaptions?.["department"] }),
			};

		case HistoryCaptions.modelVersionQuestion:
			return {
				ModelVersionID: getI18n().t("label.entityVersion", { entity: customCaptions?.["model"] }),
				ModelVersionStageID: getI18n().t("history.modelVersionChild", { model: customCaptions?.["model"], child: customCaptions?.["stage"] }),
				ModelVersionZoneID: getI18n().t("history.modelVersionChild", { model: customCaptions?.["model"], child: customCaptions?.["zone"] }),
				IsCompulsory: getI18n().t("history.isCompulsory"),
				Caption: getI18n().t("history.caption"),
				Timing: getI18n().t("history.timing"),
				Type: getI18n().t("history.type"),
				DecimalPlaces: getI18n().t("history.decimalPlaces"),
				CheckboxCaption: getI18n().t("history.checkboxCaption"),
			};
		case HistoryCaptions.modelAssets:
			return {
				ModelID: `${customCaptions?.["model"]}`,
				SiteAssetID: getI18n().t("history.siteEntity", { entity: customCaptions?.["asset"] }),
				SiteAssetRegisterID: `${customCaptions?.["asset"]}`,
				SiteAssetReferenceID: `${customCaptions?.["assetReference"]}`,
				IsActive: getI18n().t("history.isActive"),
				ModelVersionArrangementID: getI18n().t("history.modelVersionChild", { model: customCaptions?.["model"], child: customCaptions?.["arrangement"] }),
			};

		case HistoryCaptions.applicationModelStatus:
			return {
				ApplicationID: getI18n().t("history.application"),
				Name: getI18n().t("history.name"),
				Publish: getI18n().t("history.publish"),
			};
		case HistoryCaptions.applicationPosition:
			return {
				ApplicationID: getI18n().t("history.application"),
				Name: getI18n().t("history.name"),
				ModelAccess: getI18n().t("label.entityAccess", { entity: APPLICATION.ModelCC }),
				NoticeboardAccess: getI18n().t("label.entityAccess", { entity: APPLICATION.TutorialCC }),
				FeedbackAccess: getI18n().t("label.entityAccess", { entity: APPLICATION.FeedbackCC }),
				UserAccess: getI18n().t("label.entityAccess", { entity: APPLICATION.UserCC }),
				SettingsAccess: getI18n().t("history.settingsAccess"),
				ServiceAccess: getI18n().t("label.entityAccess", { entity: APPLICATION.ServiceCC }),
				DefectAccess: getI18n().t("label.entityAccess", { entity: APPLICATION.DefectCC }),
				AnalyticsAccess: getI18n().t("history.analyticsAccess"),
				AssetAccess: getI18n().t("label.entityAccess", { entity: APPLICATION.AssetCC }),
				AllowPublish: getI18n().t("history.allowPublish"),
				DefaultPage: getI18n().t("history.defaultPage"),
			};
		case HistoryCaptions.applicationPauseSubcategory:
			return {
				ApplicationID: getI18n().t("history.application"),
				Name: getI18n().t("history.name"),
			};
		case HistoryCaptions.applicationChangeStatusReason:
			return {
				ApplicationID: getI18n().t("history.application"),
				Name: getI18n().t("history.name"),
			};

		case HistoryCaptions.user:
			return {
				Email: getI18n().t("history.email"),
				FirstName: getI18n().t("history.firstName"),
				LastName: getI18n().t("history.lastname"),
				Active: getI18n().t("history.isActive"),
				IsAdmin: getI18n().t("history.isAdmin"),
				ReceiveFeedbackEmail: getI18n().t("history.receiveFeedbackEmail"),
				DisplayName: getI18n().t("history.name"),
				AdminType: getI18n().t("history.adminType"),
			};

		case HistoryCaptions.client:
			return {
				Name: getI18n().t("history.name"),
				LicensesType: getI18n().t("history.licenseType"),
				LicenseCount: getI18n().t("history.licenseCount"),
				LogoKey: getI18n().t("history.logoKey"),
				IsActive: getI18n().t("history.isActive"),
				LogoURL: getI18n().t("history.logoURL"),
				LogoFilename: getI18n().t("history.logoFileName"),
			};

		case HistoryCaptions.clientNote:
			return {
				ClientID: getI18n().t("history.client"),
				Note: getI18n().t("history.note"),
				Name: getI18n().t("history.name"),
			};

		case HistoryCaptions.clientApplication:
			return {
				ClientID: getI18n().t("history.client"),
				ApplicationID: getI18n().t("history.application"),
				IsActive: getI18n().t("history.isActive"),
			};

		case HistoryCaptions.clientDocument:
			return {
				ClientID: getI18n().t("history.client"),
				DocumentKey: getI18n().t("history.documentKey"),
				DocumentURL: getI18n().t("history.documentURL"),
				Filename: getI18n().t("history.fileName"),
			};

		case HistoryCaptions.region:
			return {
				ClientID: getI18n().t("history.client"),
				Name: getI18n().t("history.name"),
			};

		case HistoryCaptions.site:
			return {
				RegionID: getI18n().t("history.region"),
				Name: getI18n().t("history.name"),
				Company: getI18n().t("history.company"),
				Address: getI18n().t("history.address"),
				BusinessNumber: getI18n().t("history.businessNumber"),
				Licenses: getI18n().t("history.licenseCount"),
				Licensetype: getI18n().t("history.licenseType"),
				RegionName: getI18n().t("history.regionName"),
				ClientName: getI18n().t("history.clientName"),
				ClientID: getI18n().t("history.client"),
			};

		case HistoryCaptions.siteDepartment:
			return {
				SiteID: getI18n().t("history.site"),
				Name: getI18n().t("history.name"),
				Description: getI18n().t("history.description"),
			};

		case HistoryCaptions.siteAsset:
			return {
				SiteID: getI18n().t("history.site"),
				AssetNumber: customCaptions?.["asset"] || "Asset Number",
				Name: getI18n().t("history.name"),
				Description: getI18n().t("history.description"),
				Reference: customCaptions?.["assetReference"] || "Reference",
				PlannerGroup:
					customCaptions?.["assetPlannerGroup"] || "Planner Group",
				WorkCenter:
					customCaptions?.["assetMainWorkCenter"] || "Work Center",
			};

		case HistoryCaptions.applicationAction:
			return {
				ApplicationID: getI18n().t("history.application"),
				Name: getI18n().t("history.name"),
			};
		case HistoryCaptions.applicationPause:
			return {
				ApplicationID: getI18n().t("history.application"),
				Name: getI18n().t("history.name"),
			};
		case HistoryCaptions.applicationModelType:
			return {
				ApplicationID: getI18n().t("history.application"),
				Name: getI18n().t("history.name"),
			};
		case HistoryCaptions.applicationSystem:
			return {
				ApplicationID: getI18n().t("history.application"),
				Name: getI18n().t("history.name"),
			};
		case HistoryCaptions.applicationDefectType:
			return {
				ApplicationID: getI18n().t("history.application"),
				Name: getI18n().t("history.name"),
			};
		case HistoryCaptions.applicationDefectStatus:
			return {
				ApplicationID: getI18n().t("history.application"),
				Name: getI18n().t("history.name"),
				Type: getI18n().t("history.type"),
			};
		case HistoryCaptions.applicationOperatingMode:
			return {
				ApplicationID: getI18n().t("history.application"),
				Name: getI18n().t("history.name"),
			};
		case HistoryCaptions.applicationDefectRiskRating:
			return {
				ApplicationID: getI18n().t("history.application"),
				Name: getI18n().t("history.name"),
				Action: getI18n().t("history.action"),
			};
		case HistoryCaptions.applicationFeedbackStatus:
			return {
				ApplicationID: getI18n().t("history.application"),
				Name: getI18n().t("history.name"),
				Type: getI18n().t("history.type"),
			};
		case HistoryCaptions.applicationRole:
			return {
				ApplicationID: getI18n().t("history.application"),
				Name: getI18n().t("history.name"),
				CanRegisterDefect: getI18n().t("history.canRegisterEntity", { entity: APPLICATION.DefectCC }),
			};
		case HistoryCaptions.applicationFeedbackPriority:
			return {
				ApplicationID: getI18n().t("history.application"),
				Name: getI18n().t("history.name"),
			};
		case HistoryCaptions.applicationSkipTaskReason:
			return {
				ApplicationID: getI18n().t("history.application"),
				Name: getI18n().t("history.name"),
			};
		case HistoryCaptions.applicationStopReason:
			return {
				ApplicationID: getI18n().t("history.application"),
				Name: getI18n().t("history.name"),
			};
		case HistoryCaptions.applicationFeedbackClassification:
			return {
				ApplicationID: getI18n().t("history.application"),
				Name: getI18n().t("history.name"),
			};
		case HistoryCaptions.applicationMissingPartToolReason:
			return {
				ApplicationID: getI18n().t("history.application"),
				Name: getI18n().t("history.name"),
			};

		case HistoryCaptions.modelVersionArrangements:
			return {
				Name: getI18n().t("history.name"),
			};
		case HistoryCaptions.modelVersionStages:
			return {
				ModelVersionID: getI18n().t("label.entityVersion", { entity: customCaptions?.["model"] }),
				Name: getI18n().t("history.name"),
				ImageKey: getI18n().t("history.imageKey"),
				ThumbnailKey: getI18n().t("history.thumbnailKey"),
				HasZones: getI18n().t("history.hasEntity", { entity: customCaptions?.["zonePluralz"] }),
			};
		case HistoryCaptions.modelVersionZones:
			return {
				ModelVersionID: getI18n().t("label.entityVersion", { entity: customCaptions?.["model"] }),
				Name: getI18n().t("history.name"),
				ImageKey: getI18n().t("history.imageKey"),
				ThumbnailKey: getI18n().t("history.thumbnailKey"),
			};
		case HistoryCaptions.modelVersionIntervals:
			return {
				ModelVersionID: getI18n().t("label.entityVersion", { entity: customCaptions?.["model"] }),
				Name: getI18n().t("history.name"),
			};
		case HistoryCaptions.modelVersionTaskRole:
			return {
				ModelVersionTaskID: `${customCaptions?.["task"]}`,
				ModelVersionRoleID: `${customCaptions?.["role"]}`,
			};
		case HistoryCaptions.modelVersionRoles:
			return {
				ModelVersionID: getI18n().t("label.entityVersion", { entity: customCaptions?.["model"] }),
				Name: getI18n().t("history.name"),
				RoleID: `${customCaptions?.["role"]}`,
				SiteDepartmentID: getI18n().t("history.siteEntity", { entity: customCaptions?.["department"] }),
			};
		case HistoryCaptions.modelVersionQuestions:
			return {
				ModelVersionID: getI18n().t("label.entityVersion", { entity: customCaptions?.["model"] }),
				ModelVersionStageID: getI18n().t("history.modelVersionChild", { model: customCaptions?.["model"], child: customCaptions?.["stage"] }),
				ModelVersionZoneID: getI18n().t("history.modelVersionChild", { model: customCaptions?.["model"], child: customCaptions?.["zone"] }),
				IsCompulsory: getI18n().t("history.isCompulsory"),
				Caption: getI18n().t("history.caption"),
				Timing: getI18n().t("history.timing"),
				Type: getI18n().t("history.type"),
				DecimalPlaces: getI18n().t("history.decimalPlaces"),
				CheckboxCaption: getI18n().t("history.checkboxCaption"),
			};
		case HistoryCaptions.modelImportLubricant:
			return {
				ModelImportID: getI18n().t("label.entityImport", { entity: customCaptions?.["model"] }),
				Name: getI18n().t("history.name"),
				LubricantID: `${customCaptions?.["lubricant"]}`,
				NewName: getI18n().t("history.newName"),
			};
		case HistoryCaptions.modelTaskNote:
			return {
				ModelID: `${customCaptions?.["model"]}`,
				Note: getI18n().t("history.note"),
				Date: getI18n().t("history.date"),
				Name: getI18n().t("history.name"),
			};
		case HistoryCaptions.modelImportOperatingMode:
			return {
				ModelImportID: getI18n().t("label.entityImport", { entity: customCaptions?.["model"] }),
				Name: getI18n().t("history.name"),
				OperatingModeID: `${customCaptions?.["operatingMode"]}`,
				NewName: getI18n().t("history.newName"),
			};
		case HistoryCaptions.modelIntervalTaskListNo:
			return {
				ModelID: `${customCaptions?.["model"]}`,
				Name: getI18n().t("history.name"),
			};
		case HistoryCaptions.modelVersionQuestionRole:
			return {
				ModelVersionQuestionID: getI18n().t("label.entityQuestion", { entity: customCaptions?.["model"] }),
				ModelVersionRoleID: `${customCaptions?.["role"]}`,
			};
		case HistoryCaptions.modelVersionTaskStage:
			return {
				ModelVersionTaskID: `${customCaptions?.["task"]}`,
				ModelVersionStageID: `${customCaptions?.["stage"]}`,
				SiteAssetID: `${customCaptions?.["asset"]} `,
				SiteAssetRegisterID: `${customCaptions?.["asset"]}`,
				SiteAssetReferenceID: `${customCaptions?.["assetReference"]}`,
			};
		case HistoryCaptions.modelVersionTaskDocument:
			return {
				ModelVersionTaskID: `${customCaptions?.["task"]}`,
				Name: getI18n().t("history.name"),
				DocumentKey: getI18n().t("history.documentKey"),
				Link: getI18n().t("history.link"),
				Filesize: getI18n().t("history.fileSize"),
				Filename: getI18n().t("history.fileName"),
			};
		case HistoryCaptions.modelVersionTaskImage:
			return {
				ModelVersionTaskID: `${customCaptions?.["task"]}`,
				Description: getI18n().t("history.description"),
				ImageKey: getI18n().t("history.imagekey"),
				ThumbnailKey: getI18n().t("history.thumbnailkey"),
			};
		case HistoryCaptions.modelVersionTaskPermit:
			return {
				ModelVersionTaskID: `${customCaptions?.["task"]}`,
				Name: getI18n().t("history.name"),
			};
		case HistoryCaptions.modelVersionTaskZone:
			return {
				ModelVersionTaskID: `${customCaptions?.["task"]}`,
				ModelVersionZoneID: `${customCaptions?.["zone"]}`,
				SiteAssetID: `${customCaptions?.["asset"]}`,
				SiteAssetRegisterID: `${customCaptions?.["asset"]}`,
				SiteAssetReferenceID: `${customCaptions?.["assetReference"]}`,
			};
		case HistoryCaptions.modelVersionTaskInterval:
			return {
				ModelVersionTaskID: `${customCaptions?.["task"]}`,
				ModelVersionIntervalID: `${customCaptions?.["interval"]}`,
			};
		case HistoryCaptions.modelVersionTaskTool:
			return {
				ModelVersionTaskID: `${customCaptions?.["task"]}`,
				Qty: getI18n().t("history.qty"),
				Name: getI18n().t("history.name"),
			};
		case HistoryCaptions.modelVersionTaskPart:
			return {
				ModelVersionTaskID: `${customCaptions?.["task"]}`,
				Qty: getI18n().t("history.qty"),
				Name: getI18n().t("history.name"),
				Description: getI18n().t("history.description"),
				StockNumber: `${customCaptions?.["partStockNumber"]}`,
			};
		case HistoryCaptions.modelVersionsQuestionOption:
			return {
				ModelVersionQuestionID: getI18n().t("label.entityQuestion", { entity: customCaptions?.["model"] }),
				Name: getI18n().t("history.name"),
			};
		case HistoryCaptions.modelVersionIntervalInclude:
			return {
				ModelVersionIntervalID: `${customCaptions?.["model"]} ${customCaptions?.["interval"]}`,
				IncludeModelVersionIntervalID: getI18n().t("history.includeModelVersionInterval", { model: customCaptions?.["model"], interval: customCaptions?.["interval"] }),
			};
		case HistoryCaptions.modelVersionTaskQuestion:
			return {
				ModelVersionTaskID: `${customCaptions?.["task"]}`,
				IsCompulsory: getI18n().t("history.isCompulsory"),
				Caption: getI18n().t("history.caption"),
				Type: getI18n().t("history.type"),
				DecimalPlaces: getI18n().t("history.decimalPlaces"),
				MinValue: getI18n().t("history.minimumValue"),
				MaxValue: getI18n().t("history.maximumValue"),
				CheckboxCaption: getI18n().t("history.checkboxCaption"),
			};
		case HistoryCaptions.modeImportSystem:
			return {
				ModelImportID: getI18n().t("label.entityImport", { entity: customCaptions?.["model"] }),
				Name: getI18n().t("history.name"),
				SystemID: `${customCaptions?.["system"]}`,
				NewName: getI18n().t("history.newName"),
			};
		case HistoryCaptions.modeImportRole:
			return {
				ModelImportID: getI18n().t("label.entityImport", { entity: customCaptions?.["model"] }),
				Name: getI18n().t("history.name"),
				RoleID: `${customCaptions?.["role"]} `,
				NewName: getI18n().t("history.newName"),
			};
		case HistoryCaptions.modelImportAction:
			return {
				ModelImportID: getI18n().t("label.entityImport", { entity: customCaptions?.["model"] }),
				Name: getI18n().t("history.name"),
				ActionID: `${customCaptions?.["action"]} `,
				NewName: getI18n().t("history.newName"),
			};
		case HistoryCaptions.modelVersionTaskQuestionOption:
			return {
				ModelVersionTaskQuestionID: getI18n().t("label.entityQuestion", { entity: customCaptions?.["task"] }),
				Name: getI18n().t("history.name"),
				RaiseDefect: getI18n().t("history.raiseEntity", { entity: customCaptions?.["defect"] }),
			};
		case HistoryCaptions.modelImport:
			return {
				SiteAppID: getI18n().t("history.siteApplication"),
				Name: getI18n().t("history.name"),
				Model: `${customCaptions?.["model"]}`,
				SiteDepartmentID: getI18n().t("history.siteSiteAppDepartmentCC"),
				ModelTypeID: getI18n().t("history.siteappModelCCType"),
				SerialNumberRange: getI18n().t("history.serialNumberRange"),
				DocumentKey: getI18n().t("history.documentKey"),
				Source: getI18n().t("history.importSourceType"),
				SourceModelVersionID: getI18n().t("history.sourceSiteAppModelCCVersion"),
			};
		case HistoryCaptions.modelVersionTasks:
			return {
				ModelVersionID: getI18n().t("label.entityVersion", { entity: customCaptions?.["model"] }),
				ActionID: `${customCaptions?.["actionRequired"]}`,
				OperatingModeID: `${customCaptions?.["operatingMode"]}`,
				SystemID: `${customCaptions?.["system"]}`,
				LubricantID: `${customCaptions?.["lubricant"]}`,
				Name: getI18n().t("history.name"),
				SafetyCritical: `${customCaptions?.["safetyCritical"]}`,
				EstimatedMinutes: getI18n().t("history.estimatedMinutes"),
				NotSkippable: getI18n().t("history.cannotSkipEntity", { entity: customCaptions?.["taskPlural"] }),
				LubricantVolume: getI18n().t("label.entityVolume", { entity: customCaptions?.["lubricant"] }),
				LubricantUOM: getI18n().t("label.entityUOM", { entity: customCaptions?.["lubricant"] }),
				Purpose: `${customCaptions?.["purpose"]}`,
				Procedure: `${customCaptions?.["procedure"]}`,
				PossibleHazards: `${customCaptions?.["possibleHazardsPlural"]}`,
				AdditionalPPE: `${customCaptions?.["additionalPPE"]}`,
				Specification: `${customCaptions?.["specification"]}`,
				ContaminationControl: `${customCaptions?.["contaminationControls"]}`,
				OtherInformation: `${customCaptions?.["otherInformation"]}`,
				CorrectiveActions: `${customCaptions?.["correctiveActionsPlural"]}`,
				CustomField1: `${customCaptions?.["customField1"]}`,
				CustomField2: `${customCaptions?.["customField2"]}`,
				CustomField3: `${customCaptions?.["customField3"]}`,
				Controls: `${customCaptions?.["controlsPlural"]}`,
				Isolations: `${customCaptions?.["isolationsPlural"]}`,
				SiteID: getI18n().t("history.site"),
				SiteAssetID: `${customCaptions?.["asset"]}`,
				SiteAssetRegisterID: `${customCaptions?.["asset"]}`,
				SiteAssetReferenceID: `${customCaptions?.["assetReference"]}`,
			};

		case HistoryCaptions.defects:
			return {
				ModelID: `${customCaptions?.["model"]}`,
				ModelVersionStageID: `${customCaptions?.["stage"]}`,
				ModelVersionZoneID: `${customCaptions?.["zone"]}`,
				ServiceID: `${customCaptions?.["service"]}`,
				SiteDepartmentID: `${customCaptions?.["department"]}`,
				ServiceTaskID: `${customCaptions?.["task"]}`,
				ServiceTaskQuestionID: `${customCaptions?.["task"]} ${customCaptions?.["question"]}`,
				SiteAssetID: `${customCaptions?.["asset"]}`,
				SiteAssetRegisterID: `${customCaptions?.["asset"]}`,
				SiteAssetReferenceID: `${customCaptions?.["assetReference"]}`,
				DefectRiskRatingID: `${customCaptions?.["riskRating"]}`,
				DefectStatusID: `${customCaptions?.["defectStatus"]}`,
				DefectTypeID: `${customCaptions?.["defectType"]}`,
				WorkOrder: `${customCaptions?.["defectWorkOrder"]}`,
				Details: getI18n().t("history.details"),
			};
		case HistoryCaptions.defectNote:
			return {
				DefectID: `${customCaptions?.["defect"]}`,
				Note: getI18n().t("history.note"),
				Date: getI18n().t("history.date"),
				Name: getI18n().t("history.name"),
			};
		case HistoryCaptions.defectImage:
			return {
				DefectID: `${customCaptions?.["defect"]}`,
				ImageKey: getI18n().t("history.imageKey"),
				ThumbnailKey: getI18n().t("history.thumbnailKey"),
			};
		case HistoryCaptions.defectPart:
			return {
				DefectID: `${customCaptions?.["defect"]}`,
				Qty: getI18n().t("history.qty"),
				Name: getI18n().t("history.name"),
				Description: getI18n().t("history.description"),
			};

		case HistoryCaptions.clientUser:
			return {
				UserID: getI18n().t("history.user"),
				ClientID: getI18n().t("history.client"),
				IsAdmin: getI18n().t("history.isAdmin"),
				Active: getI18n().t("history.isActive"),
				ExternalReference: getI18n().t("history.externalReference"),
			};
		case HistoryCaptions.clientUserSite:
			return {
				Active: getI18n().t("history.isActive"),
				AllowAllModels: getI18n().t("history.allowAllEntity", { entity: customCaptions?.["modelPlural"] }),
				PositionID: `${customCaptions?.["position"]}`,
				clientUserID: getI18n().t("history.clientuser"),
				SiteID: getI18n().t("history.site"),
				SiteDepartmentID: getI18n().t("history.siteEntity", { entity: APPLICATION?.["DepartmentCC"] }),
			};
		case HistoryCaptions.clientUserSiteAppServiceModel:
			return {
				ClientUserSiteAppID: `${customCaptions?.["user"]}`,
				ModelID: `${customCaptions?.["model"]}`,
			};

		case HistoryCaptions.clientUserSiteApp:
			return {
				ClientUserSiteID: getI18n().t("history.clientUserSite"),
				SiteAppID: getI18n().t("history.siteApplication"),
				PositionID: getI18n().t("history.position"),
				AllowAllModels: getI18n().t("history.allowAllEntity", { entity: customCaptions?.["modelPlural"] }),
			};

		case HistoryCaptions.clientUserNote:
			return {
				ClientUserID: getI18n().t("history.clientUser"),
				Note: getI18n().t("history.note"),
				Date: getI18n().t("history.date"),
				Name: getI18n().t("history.name"),
			};

		case HistoryCaptions.clientUserSiteAppServiceDepartment:
			return {
				ClientUserSiteAppID: getI18n().t("history.clientUserSiteApplication"),
				SiteDepartmentID: getI18n().t("history.siteEntity", { entity: customCaptions?.["department"] }),
			};

		case HistoryCaptions.clientUserSiteAppServiceRole:
			return {
				ClientUserSiteAppID: getI18n().t("history.clientUserSiteApplication"),
				RoleID: getI18n().t("history.role"),
			};

		case HistoryCaptions.model:
			return {
				SiteAppID: getI18n().t("history.siteApplication"),
				DevModelVersionID: getI18n().t("label.developmentEntityVersion", {
				entity: customCaptions?.["model"],
			}),
				ActiveModelVersionID: getI18n().t("label.activeEntityVersion", {
				entity: customCaptions?.["model"],
			}),
				Type: getI18n().t("history.type"),
				IsSharable: getI18n().t("history.isSharable"),
				Active: getI18n().t("history.isActive"),
			};
		case HistoryCaptions.services:
			return {
				SiteAppID: getI18n().t("history.siteApplication"),
				ModelID: `${customCaptions?.["model"]}`,
				WorkOrder: `${customCaptions?.["serviceWorkOrder"]}`,
				ModelVersionIntervalID: `${customCaptions?.["interval"]}`,
				ModelVersionArrangementID: `${customCaptions?.["arrangement"]}`,
				ModelVersionRoleID: `${customCaptions?.["role"]}`,
				SiteDepartmentID: `${customCaptions?.["department"]}`,
				SiteAssetID: `${customCaptions?.["asset"]}`,
				SiteAssetRegisterID: `${customCaptions?.["asset"]}`,
				SiteAssetReferenceID: `${customCaptions?.["assetReference"]}`,
				Status: getI18n().t("history.status"),
				ScheduledDate: getI18n().t("history.scheduledDate"),
				StartDate: getI18n().t("history.startDate"),
				CompletedDate: getI18n().t("history.completedDate"),
				NotificationNumber: getI18n().t("history.notificationNumber"),
				ClientName: getI18n().t("history.clientName"),
				CompletedEstimatedMinutes: getI18n().t("history.completedEstimatedMinutes"),
				SignatureURL: getI18n().t("history.signatureURL"),
				PercentageComplete: getI18n().t("history.percentageComplete"),
				PercentageOverTime: getI18n().t("history.percentageOverTime"),
				MinutesOverTime: getI18n().t("history.minutesOvertime"),
				StatusName: getI18n().t("history.statusName"),
			};
		case HistoryCaptions.serviceTask:
			return {
				SiteAppID: getI18n().t("history.siteApplication"),
				ModelVersionStageID: `${customCaptions?.["stage"]}`,
				ModelVersionZoneID: `${customCaptions?.["zone"]}`,
				ModelVersionTaskID: ` ${customCaptions?.["task"]}`,
				ServiceSessionID: getI18n().t("label.entitySession", { entity: customCaptions?.["service"] }),
				SiteAssetID: `${customCaptions?.["asset"]}`,
				SiteAssetRegisterID: `${customCaptions?.["asset"]}`,
				SiteAssetReferenceID: `${customCaptions?.["assetReference"]}`,
				SiteAsset: `${customCaptions?.["asset"]}`,
				CompletedDate: getI18n().t("history.completedDate"),
				ActualMinutes: getI18n().t("history.actualMinutes"),
				IsSkipped: getI18n().t("history.isSkipped"),
				SkipTaskReasonID: getI18n().t("label.entityReason", { entity: customCaptions?.["task"] }),
				SkipOtherReason: getI18n().t("history.skipOtherReason"),
			};

		case HistoryCaptions.siteApp:
			return SiteApp;
		case HistoryCaptions.serviceSession:
			return {
				ServiceID: `${customCaptions?.["service"]}`,
				UserID: `${customCaptions?.["user"]}`,
				StartDate: getI18n().t("history.startDate"),
				EndDate: getI18n().t("history.endDate"),
				StopReasonID: getI18n().t("history.stopReason"),
				OtherReason: getI18n().t("history.otherReason"),
				SignatureKey: getI18n().t("history.signatureKey"),
			};
		case HistoryCaptions.serviceTaskQuestion:
			return {
				ServiceTaskID: `${customCaptions?.["task"]}`,
				ModelVersionTaskQuestionID: getI18n().t("label.entityQuestion", { entity: customCaptions?.["task"] }),
				Date: getI18n().t("history.date"),
			};
		case HistoryCaptions.serviceTaskQuestionOption:
			return {
				ServiceTaskQuestionID: getI18n().t("label.entityQuestion", { entity: customCaptions?.["task"] }),
				ModelVersionTaskQuestionOptionID: getI18n().t("history.option"),
			};
		case HistoryCaptions.serviceSessionQuestion:
			return {
				ServiceSessionID: getI18n().t("label.entitySession", { entity: customCaptions?.["service"] }),
				ModelVersionQuestionID: getI18n().t("history.question"),
				Date: getI18n().t("history.date"),
			};
		case HistoryCaptions.serviceSessionQuestionOption:
			return {
				ServiceSessionQuestionID: getI18n().t("label.entitySessionQuestion", { entity: customCaptions?.["service"] }),
				ModelVersionQuestionOptionID: getI18n().t("history.questionOption"),
			};
		case HistoryCaptions.serviceNote:
			return {
				ServiceID: `${customCaptions?.["service"]}`,
				Note: getI18n().t("history.note"),
				Date: getI18n().t("history.date"),
				Name: getI18n().t("history.name"),
			};
		case HistoryCaptions.serviceMissingPartTool:
			return {
				ServiceID: `${customCaptions?.["service"]}`,
				ServiceSessionID: getI18n().t("label.entitySession", { entity: customCaptions?.["service"] }),
				MissingPartToolReasonID: getI18n().t("label.missingPartToolReason", { part: customCaptions?.["part"], tool: customCaptions?.["tool"] }),
				ModelVersionTaskPartID: `${APPLICATION?.["PartCC"]}`,
				ModelVersionTaskToolID: `${APPLICATION?.["ToolCC"]}`,
				OtherReason: getI18n().t("history.otherReason"),
				DateTime: getI18n().t("history.date"),
			};
		case HistoryCaptions.servicePause:
			return {
				ServiceID: `${customCaptions?.["service"]}`,
				ServiceSessionID: getI18n().t("history.session"),
				EndDate: getI18n().t("history.endDate"),
				PauseID: `${customCaptions?.["pauseReason"]}`,
				ActionID: `${customCaptions?.["action"]}`,
				NewName: getI18n().t("history.newName"),
				PauseSubcategoryID: getI18n().t("history.pauseSubcategory"),
				OtherReason: getI18n().t("history.otherReason"),
				PauseMinutes: getI18n().t("history.pauseMinutes"),
			};
		case HistoryCaptions.serviceStatusChange:
			return {
				ServiceID: `${customCaptions?.["service"]}`,
				Status: getI18n().t("history.status"),
				ChangeStatusReasonID: getI18n().t("history.changeStatusReason"),
				OtherReason: getI18n().t("history.otherReason"),
			};
		case HistoryCaptions.feedback:
			return {
				FeedbackClassificationID: `${customCaptions?.["classification"]}`,
				FeedbackPriorityID: `${customCaptions?.["priority"]}`,
				FeedbackStatusID: `${customCaptions?.["feedbackStatus"]}`,
				ModelID: `${APPLICATION?.["ModelCC"]}`,
				ModelVersionStageID: `${customCaptions?.["stage"]}`,
				ModelVersionZoneID: `${customCaptions?.["zone"]}`,
				ModelVersionTaskID: ` ${customCaptions?.["task"]}`,
				AssignUserID: getI18n().t("history.assignEntity", { entity: customCaptions?.["user"] }),
				AssignPositionID: `${customCaptions?.["position"]}`,
				SiteDepartmentID: `${customCaptions?.["department"]}`,
				SiteAssetID: `${customCaptions?.["asset"]}`,
				SiteAssetRegisterID: `${customCaptions?.["asset"]}`,
				SiteAssetReferenceID: `${customCaptions?.["assetReference"]}`,
				ChangeRequired: `${customCaptions?.["changeRequired"]}`,
				Benefit: `${customCaptions?.["benefit"]}`,
				ModelVersionRoleID: `${customCaptions?.["role"]}`,
				ModelVersionIntervalID: `${customCaptions?.["interval"]}`,
			};
		case HistoryCaptions.feedbackNote:
			return {
				FeedbackID: `${customCaptions?.["feedback"]}`,
				Note: getI18n().t("history.note"),
				Date: getI18n().t("history.date"),
				Name: getI18n().t("history.name"),
			};
		case HistoryCaptions.feedbackImage:
			return {
				FeedbackID: `${customCaptions?.["feedback"]}`,
				ImageKey: getI18n().t("history.imageKey"),
				ThumbnailKey: getI18n().t("history.thumbnailKey"),
			};
		case HistoryCaptions.feedbackDocument:
			return {
				FeedbackID: `${customCaptions?.["feedback"]}`,
				Name: getI18n().t("history.name"),
				DocumentKey: getI18n().t("history.documentKey"),
				DocumentURL: getI18n().t("history.documentURL"),
				Filename: getI18n().t("history.fileName"),
				Date: getI18n().t("history.date"),
			};

		case HistoryCaptions.noticeBoard:
			return {
				Name: getI18n().t("history.name"),
				Description: getI18n().t("history.description"),
				Link: getI18n().t("history.link"),
				ExpiryDate: getI18n().t("history.expiryDate"),
			};
		case HistoryCaptions.modelVersionDepartment:
			return {
				ModelVersionID: getI18n().t("label.entityVersion", {
				entity: customCaptions?.["model"],
			}),
				SiteDepartmentID: ` ${customCaptions?.["department"]}`,
			};
		case HistoryCaptions.noticeBoardDepartment:
			return {
				NoticeboardID: ` ${customCaptions?.["tutorial"]}`,
				SiteDepartmentID: ` ${customCaptions?.["department"]}`,
			};
		case HistoryCaptions.modelVersionArrangement:
			return {
				ModelVersionID: getI18n().t("label.entityVersion", {
				entity: customCaptions?.["model"],
			}),
				Name: getI18n().t("history.name"),
			};
		case HistoryCaptions.modelVersionTaskArrangement:
			return {
				ModelVersionTaskID: ` ${customCaptions?.["task"]}`,
				ModelVersionArrangementID: `${customCaptions?.["arrangement"]}`,
			};
		case HistoryCaptions.modelError:
			return {
				ServiceID: `${customCaptions?.["service"]}`,
				UserID: `${customCaptions?.["user"]}`,
				ServiceTaskID: `${customCaptions?.["task"]}`,
				Error: getI18n().t("history.error"),
			};

		case HistoryCaptions.pause:
			return {
				SiteAppID: getI18n().t("history.siteApplication"),
				Name: getI18n().t("history.name"),
			};

		case HistoryCaptions.pauseSubcategory:
			return {
				PauseID: getI18n().t("history.pause"),
				Name: getI18n().t("history.name"),
			};

		case HistoryCaptions.missingPartToolReason:
			return {
				SiteAppID: getI18n().t("history.siteApplication"),
				Name: getI18n().t("history.name"),
			};

		case HistoryCaptions.siteAssetReference:
			return {
				SiteAssetID: getI18n().t("history.siteEntityBracket", { entity: APPLICATION.AssetCC }),
				Name: getI18n().t("history.name"),
				Description: getI18n().t("history.description"),
				PlannerGroup: getI18n().t("history.plannerGroup"),
				WorkCenter: getI18n().t("history.workCenter"),
			};

		case HistoryCaptions.report:
			return {
				SiteAppID: getI18n().t("history.siteApplication"),
				ReportID: getI18n().t("history.report"),
				Name: getI18n().t("history.name"),
				Type: getI18n().t("history.reportType"),
			};

		case HistoryCaptions.skipTaskReason:
			return {
				SiteAppID: getI18n().t("history.siteApplication"),
				Name: getI18n().t("history.name"),
			};

		case HistoryCaptions.stopReason:
			return {
				SiteAppID: getI18n().t("history.siteApplication"),
				Name: getI18n().t("history.name"),
			};

		case HistoryCaptions.changeStatusReason:
			return {
				SiteAppID: getI18n().t("history.siteApplication"),
				Name: getI18n().t("history.name"),
			};

		case HistoryCaptions.modelStatus:
			return {
				SiteAppID: getI18n().t("history.siteApplication"),
				Name: getI18n().t("history.name"),
				Publish: getI18n().t("history.publish"),
			};

		case HistoryCaptions.modelType:
			return {
				SiteAppID: getI18n().t("history.siteApplication"),
				Name: getI18n().t("history.name"),
				Code: getI18n().t("history.code"),
			};

		case HistoryCaptions.action:
			return {
				SiteAppID: getI18n().t("history.siteApplication"),
				Name: getI18n().t("history.name"),
			};

		case HistoryCaptions.system:
			return {
				SiteAppID: getI18n().t("history.siteApplication"),
				Name: getI18n().t("history.name"),
				Code: getI18n().t("history.code"),
			};

		case HistoryCaptions.operatingMode:
			return {
				SiteAppID: getI18n().t("history.siteApplication"),
				Name: getI18n().t("history.name"),
			};

		case HistoryCaptions.lubricant:
			return {
				SiteAppID: getI18n().t("history.siteApplication"),
				Name: getI18n().t("history.name"),
			};

		case HistoryCaptions.role:
			return {
				SiteAppID: getI18n().t("history.siteApplication"),
				Name: getI18n().t("history.name"),
			};

		case HistoryCaptions.position:
			return {
				SiteAppID: getI18n().t("history.siteApplication"),
				Name: getI18n().t("history.name"),
				ModelAccess: getI18n().t("history.siteAppEntityAccess", { entity: APPLICATION.ModelCC }),
				NoticeboardAccess: getI18n().t("history.siteAppEntityAccess", { entity: APPLICATION.TutorialCC }),
				FeedbackAccess: getI18n().t("history.siteAppEntityAccess", { entity: APPLICATION.FeedbackCC }),
				UserAccess: getI18n().t("history.siteAppEntityAccess", { entity: APPLICATION.UserCC }),
				SettingsAccess: getI18n().t("history.settingsAccess"),
				ServiceAccess: getI18n().t("history.siteAppEntityAccess", { entity: APPLICATION.ServiceCC }),
				DefectAccess: getI18n().t("history.siteAppEntityAccess", { entity: APPLICATION.DefectCC }),
				AnalyticsAccess: getI18n().t("history.analyticsAccess"),
				AssetAccess: getI18n().t("history.siteAppEntityAccess", { entity: APPLICATION.AssetCC }),
				AllowPublish: getI18n().t("history.allowPublish"),
				DefaultPage: getI18n().t("history.defaultPage"),
			};

		case HistoryCaptions.defectRiskRating:
			return {
				SiteAppID: getI18n().t("history.siteApplication"),
				Name: getI18n().t("history.name"),
				Action: getI18n().t("history.siteAppEntity", { entity: APPLICATION.ActionCC }),
			};

		case HistoryCaptions.defectStatus:
			return {
				SiteAppID: getI18n().t("history.siteApplication"),
				Name: getI18n().t("history.name"),
				Type: getI18n().t("history.type"),
			};

		case HistoryCaptions.defectType:
			return {
				SiteAppID: getI18n().t("history.siteApplication"),
				Name: getI18n().t("history.name"),
			};

		case HistoryCaptions.feedbackClassification:
			return {
				SiteAppID: getI18n().t("history.siteApplication"),
				Name: getI18n().t("history.name"),
			};

		case HistoryCaptions.feedbackPriority:
			return {
				SiteAppID: getI18n().t("history.siteApplication"),
				Name: getI18n().t("history.name"),
			};

		case HistoryCaptions.feedbackStatus:
			return {
				SiteAppID: getI18n().t("history.siteApplication"),
				Name: getI18n().t("history.name"),
				Type: getI18n().t("history.type"),
			};

		case HistoryCaptions.siteAppKeyContact:
			return {
				SiteAppID: getI18n().t("history.siteApplication"),
				UserID: getI18n().t("history.user"),
			};

		default:
			return;
	}
};

// Flat fallback map of audit field names -> localised labels, used when the
// per-entity HistoryCustomCaption case does not cover a field (or `origin` is
// unset). Field names are the raw audited property names sent by the backend.
const buildHistoryFieldLabels = () => ({
		Action: getI18n().t("history.action"),
		Active: getI18n().t("history.isActive"),
		Address: getI18n().t("history.address"),
		AdminType: getI18n().t("history.adminType"),
		AllowPublish: getI18n().t("history.allowPublish"),
		AnalyticsAccess: getI18n().t("history.analyticsAccess"),
		ApplicationID: getI18n().t("history.application"),
		BusinessNumber: getI18n().t("history.businessNumber"),
		Caption: getI18n().t("history.caption"),
		ChangeStatusReasonID: getI18n().t("history.changeStatusReason"),
		CheckboxCaption: getI18n().t("history.checkboxCaption"),
		ClientID: getI18n().t("history.client"),
		ClientName: getI18n().t("history.clientName"),
		ClientUserID: getI18n().t("history.clientUser"),
		ClientUserSiteAppID: getI18n().t("history.clientUserSiteApplication"),
		ClientUserSiteID: getI18n().t("history.clientUserSite"),
		Code: getI18n().t("history.code"),
		Company: getI18n().t("history.company"),
		CompletedEstimatedMinutes: getI18n().t("history.completedEstimatedMinutes"),
		Date: getI18n().t("history.date"),
		DateTime: getI18n().t("history.date"),
		DecimalPlaces: getI18n().t("history.decimalPlaces"),
		DefaultPage: getI18n().t("history.defaultPage"),
		Description: getI18n().t("history.description"),
		DisplayName: getI18n().t("history.name"),
		DocumentKey: getI18n().t("history.documentKey"),
		DocumentURL: getI18n().t("history.documentURL"),
		Email: getI18n().t("history.email"),
		EndDate: getI18n().t("history.endDate"),
		Error: getI18n().t("history.error"),
		EstimatedMinutes: getI18n().t("history.estimatedMinutes"),
		ExpiryDate: getI18n().t("history.expiryDate"),
		ExternalReference: getI18n().t("history.externalReference"),
		Filename: getI18n().t("history.fileName"),
		Filesize: getI18n().t("history.fileSize"),
		FirstName: getI18n().t("history.firstName"),
		ImageKey: getI18n().t("history.imageKey"),
		ImageURL: getI18n().t("history.imageURL"),
		IsActive: getI18n().t("history.isActive"),
		IsAdmin: getI18n().t("history.isAdmin"),
		IsCompulsory: getI18n().t("history.isCompulsory"),
		IsPublished: getI18n().t("history.published"),
		IsSharable: getI18n().t("history.isSharable"),
		LastName: getI18n().t("history.lastname"),
		LicenseCount: getI18n().t("history.licenseCount"),
		LicenseType: getI18n().t("history.licenseType"),
		Licenses: getI18n().t("history.licenseCount"),
		LicensesType: getI18n().t("history.licenseType"),
		Licensetype: getI18n().t("history.licenseType"),
		Link: getI18n().t("history.link"),
		LogoFilename: getI18n().t("history.logoFileName"),
		LogoKey: getI18n().t("history.logoKey"),
		LogoURL: getI18n().t("history.logoURL"),
		MaxValue: getI18n().t("history.maximumValue"),
		MinValue: getI18n().t("history.minimumValue"),
		MinutesOverTime: getI18n().t("history.minutesOvertime"),
		ModelTypeID: getI18n().t("history.siteappModelCCType"),
		ModelVersionQuestionID: getI18n().t("history.question"),
		ModelVersionQuestionOptionID: getI18n().t("history.questionOption"),
		ModelVersionTaskQuestionOptionID: getI18n().t("history.option"),
		Name: getI18n().t("history.name"),
		NewName: getI18n().t("history.newName"),
		Note: getI18n().t("history.note"),
		OtherReason: getI18n().t("history.otherReason"),
		PauseID: getI18n().t("history.pause"),
		PauseMinutes: getI18n().t("history.pauseMinutes"),
		PauseSubcategoryID: getI18n().t("history.pauseSubcategory"),
		PercentageComplete: getI18n().t("history.percentageComplete"),
		PercentageOverTime: getI18n().t("history.percentageOverTime"),
		PlannerGroup: getI18n().t("history.plannerGroup"),
		PositionID: getI18n().t("history.position"),
		Publish: getI18n().t("history.publish"),
		Qty: getI18n().t("history.qty"),
		ReceiveFeedbackEmail: getI18n().t("history.receiveFeedbackEmail"),
		RegionID: getI18n().t("history.region"),
		RegionName: getI18n().t("history.regionName"),
		ReportID: getI18n().t("history.report"),
		ReviewDate: getI18n().t("history.reviewDate"),
		RoleID: getI18n().t("history.role"),
		SerialNumberRange: getI18n().t("history.serialNumberRange"),
		ServiceSessionID: getI18n().t("history.session"),
		SettingsAccess: getI18n().t("history.settingsAccess"),
		ShareModels: getI18n().t("history.shareModels"),
		ShowTimer: getI18n().t("history.showTimer"),
		SignatureKey: getI18n().t("history.signatureKey"),
		SignatureURL: getI18n().t("history.signatureURL"),
		SiteAppID: getI18n().t("history.siteApplication"),
		SiteDepartmentID: getI18n().t("history.siteSiteAppDepartmentCC"),
		SiteID: getI18n().t("history.site"),
		Source: getI18n().t("history.importSourceType"),
		SourceModelVersionID: getI18n().t("history.sourceSiteAppModelCCVersion"),
		StartDate: getI18n().t("history.startDate"),
		Status: getI18n().t("history.status"),
		StatusName: getI18n().t("history.statusName"),
		StopReasonID: getI18n().t("history.stopReason"),
		ThumbnailKey: getI18n().t("history.thumbnailKey"),
		ThumbnailURL: getI18n().t("history.thumbnailImageURL"),
		Timing: getI18n().t("history.timing"),
		Type: getI18n().t("history.type"),
		UserID: getI18n().t("history.user"),
		Version: getI18n().t("history.version"),
		WorkCenter: getI18n().t("history.workCenter"),
		clientUserID: getI18n().t("history.clientuser"),
		roleID: getI18n().t("history.role2"),
	});

// Consumers call HistoryFieldLabels() per audit row per render; rebuilding the
// ~125-entry t() map each time is O(rows x entries). Cache per language and
// invalidate when a late-arriving bundle adds resources.
let historyFieldLabelsCache = { language: null, labels: null };
getI18n()?.store?.on?.("added", () => {
	historyFieldLabelsCache = { language: null, labels: null };
});
export const HistoryFieldLabels = () => {
	const language = getI18n()?.language ?? "en";
	if (historyFieldLabelsCache.language !== language) {
		historyFieldLabelsCache = { language, labels: buildHistoryFieldLabels() };
	}
	return historyFieldLabelsCache.labels;
};


// Audit field name -> { raw enum code : localised label key }. Wired into
// historyFinalData so enum-valued old/new values in the change history localise
// instead of showing the raw backend enum name. Extend as more enum-valued
// audited fields surface.
export const HistoryValueLabels = {
	LicenseType: {
		Undefined: "label.unknownLicense",
		TotalUsers: "label.totalUsers",
		ConcurrentUsers: "label.concurrentUsers",
		PerRoleWorkOrder: "label.perRoleWorkOrder",
		SiteBasedLicensing: "label.siteBasedLicensing",
		ApplicationBasedLicensing: "label.applicationBasedLicensing",
	},
	LicensesType: {
		Undefined: "label.unknownLicense",
		TotalUsers: "label.totalUsers",
		ConcurrentUsers: "label.concurrentUsers",
		PerRoleWorkOrder: "label.perRoleWorkOrder",
		SiteBasedLicensing: "label.siteBasedLicensing",
		ApplicationBasedLicensing: "label.applicationBasedLicensing",
	},
	// Site audit rows use this exact casing (see the site entry map above).
	Licensetype: {
		Undefined: "label.unknownLicense",
		TotalUsers: "label.totalUsers",
		ConcurrentUsers: "label.concurrentUsers",
		PerRoleWorkOrder: "label.perRoleWorkOrder",
		SiteBasedLicensing: "label.siteBasedLicensing",
		ApplicationBasedLicensing: "label.applicationBasedLicensing",
	},
	ShareModels: {
		None: "label.shareNone",
		AllSites: "label.shareAllSites",
		AllClients: "label.shareAllClients",
	},
};

export const HistoryProperty = {
	serviceStatus: "serviceStatus",
	questionType: "questionType",
	questionTiming: "questionTiming",
};

export const HistoryPropertyCode = (from, customCaptions) => {
	switch (from) {
		case HistoryProperty.questionTiming:
			return {
				B: getI18n().t("history.beginningOfEntity", { entity: customCaptions["service"] }),
				E: getI18n().t("history.endOfEntity", { entity: customCaptions["service"] }),
				S: getI18n().t("history.beginningOfEntity", { entity: customCaptions["stage"] }),
				Z: getI18n().t("history.beginningOfEntity", { entity: customCaptions["zone"] }),
			};
		case HistoryProperty.questionType:
			return {
				S: getI18n().t("label.shortText"),
				B: getI18n().t("label.boolean"),
				N: getI18n().t("label.number"),
				L: getI18n().t("label.longText"),
				T: getI18n().t("label.time"),
				D: getI18n().t("label.date"),
				O: getI18n().t("label.dropdownList"),
				C: getI18n().t("label.list"),
			};
		case HistoryProperty.serviceStatus:
			return {
				T: getI18n().t("label.stopped"),
				I: getI18n().t("label.inProgress"),
				N: getI18n().t("label.incomplete"),
				C: getI18n().t("label.complete"),
				X: getI18n().t("label.cancelled"),
				H: getI18n().t("label.checkedOut"),
				M: getI18n().t("label.incomplete"),
				P: getI18n().t("label.completedByPaper"),
				S: getI18n().t("label.scheduled"),
			};
		default:
			return;
	}
};

export const DETAILS = "Details";
export const ASSETS = "Assets";
export const DEPARTMENTS = "Departments";

export const siteScreenNavigation = [
	{ name: DETAILS, url: `/${siteDetailPath}` },
	{ name: ASSETS, url: siteAssetPath },
	{ name: DEPARTMENTS, url: siteDepartmentPath },
];

export const modelScreenNavigation = ({
	assests,
	stages,
	zones,
	intervals,
	roles,
	questions,
	tasks,
}) => [
		{ name: "Details", url: modelDetail },
		{ name: `Assests(${assests})`, url: modelAssest },
		{ name: `Stages(${stages})`, url: modelStages },
		{ name: `Zones(${zones})`, url: modelZones },
		{ name: `Intervals(${intervals})`, url: modelIntervals },
		{ name: `Roles(${roles})`, url: modelRoles },
		{ name: `Questions(${questions})`, url: modelQuestions },
		{ name: `Tasks(${tasks})`, url: modelTask },
		{ name: "Service Layout", url: modelServiceLayout },
	];

export const defectStatusTypes = [
	{
		get label() {
			return getI18n().t("label.outstanding");
		},
		value: "O",
	},
	{
		get label() {
			return getI18n().t("action.complete");
		},
		value: "C",
	},
];

// Canonical F/E/R/N access-type map. helpers/positionAccessTypes re-exports
// this object, so the two import paths cannot drift. Getters keep t() lazy -
// importing i18n at module top-level here recreates the BASE_API_PATH cycle.
export const positionAccessTypes = {
	get F() {
		return getI18n().t("label.full");
	},
	get E() {
		return getI18n().t("action.edit");
	},
	get R() {
		return getI18n().t("label.readOnly");
	},
	get N() {
		return getI18n().t("label.none");
	},
};

export const NoReadOnly = ["F", "E"];
export const AccessTypes = {
	Full: "F",
	Edit: "E",
	"Read-Only": "R",
	None: "N",
};

export const DefaultPageOptions = (cc) => {
	return {
		6: getI18n().t("label.analytics"),
		5: cc?.defectPlural ?? getI18n().t("label.defects"),
		2: cc?.feedbackPlural ?? getI18n().t("label.feedback"),
		0: cc?.modelPlural ?? getI18n().t("label.models"),
		1: cc?.tutorialPlural ?? getI18n().t("label.noticeBoards"),
		4: cc?.servicePlural ?? getI18n().t("label.services"),
		3: cc?.userPlural ?? getI18n().t("label.users"),
	};
};

export const defaultRedirect = {
	6: analysisPath,
	5: `/app/${defectsPath}`,
	2: `/app/${feedbackPath}`,
	0: `/app/${modelsPath}`,
	1: "/app/noticeboards",
	4: `/app/${servicesPath}`,
	3: `/app/{usersPath}`,
};

export const pageSize = {
	SMALL_SIZE: 15,
	MEDIUM_SIZE: 20,
	LARGE_SIZE: 25,
};

export const NotificationAlerts = {
	success: "#24BA78",
	error: "#E31212",
	info: "#307AD6",
	warning: "#ED8738",
};

export const workbookFields = (customCaptions) => [
	{ id: "1", name: "purpose", label: `${customCaptions.purpose}` },
	{ id: "2", name: "procedure", label: `${customCaptions.procedure}` },
	{
		id: "3",
		name: "possibleHazards",
		label: `${customCaptions.possibleHazardsPlural}`,
	},
	{
		id: "4",
		name: "additionalPPE",
		label: `${customCaptions.additionalPPE}`,
	},
	{
		id: "5",
		name: "specification",
		label: `${customCaptions.specification}`,
	},
	{
		id: "6",
		name: "contaminationControl",
		label: `${customCaptions.contaminationControlsPlural}`,
	},
	{
		id: "7",
		name: "otherInformation",
		label: `${customCaptions.otherInformation}`,
	},
	{
		id: "8",
		name: "correctiveActions",
		label: `${customCaptions.correctiveActionsPlural}`,
	},
	{
		id: "9",
		name: "isolations",
		label: `${customCaptions.isolationsPlural}`,
	},
	{ id: "10", name: "controls", label: `${customCaptions.controlsPlural}` },
	{ id: "11", name: "customField1", label: `${customCaptions.customField1}` },
	{ id: "12", name: "customField2", label: `${customCaptions.customField2}` },
	{ id: "13", name: "customField3", label: `${customCaptions.customField3}` },
];

export const DROPDOWN_TOP_OFFSET = 30;
export const DROPDOWN_LEFT_OFFSET = 30;
export const DROPDOWN_RIGHT_OFFSET = 30;

export const statusOptions = () => [
	{ id: "X", name: getI18n().t("label.cancelled"), groupBy: "Complete" },
	{ id: "C", name: getI18n().t("action.complete"), groupBy: "Complete" },
	{ id: "P", name: getI18n().t("action.completedByPaper"), groupBy: "Complete" },
	{ id: "H", name: getI18n().t("label.checkedOut"), groupBy: "Not Complete" },
	{ id: "I", name: getI18n().t("label.inProgress"), groupBy: "Not Complete" },
	{ id: "N", name: getI18n().t("action.incomplete"), groupBy: "Complete" },
	{ id: "S", name: getI18n().t("label.scheduled"), groupBy: "Not Complete" },
	{ id: "T", name: getI18n().t("label.stopped"), groupBy: "Not Complete" },
];

export const statusTypeClassification = { 1: "C", 2: "O" };

// defects storage
export const DEFECTS_STORAGE_STATUS = "defects-status";
export const DEFECTS_STORAGE_DEPARTMENT = "defects-department";
export const DEFECTS_STORAGE_TIMEFRAME = "defects-timeFrame";

// feedback storage
export const FEEDBACK_STORAGE_STATUS = "feedback-status";
export const FEEDBACK_STORAGE_DEPARTMENT = "feedback-department";
export const FEEDBACK_STORAGE_TIMEFRAME = "feedback-timeFrame";
export const FEEDBACK_STORAGE_MY_FEEDBACK = "feedback-myfeedback";

//service storage
export const SERVICE_STORAGE_STATUS = "service-status";
export const SERVICE_STORAGE_DEPARTMENT = "service-department";
export const SERVICE_STORAGE_TIMEFRAME = "service-timeFrame";

//model storage
export const MODEL_STORAGE_STATUS = "model-status";
export const MODEL_STORAGE_DEPARTMENT = "model-department";

export const REMOVE_SESSIONS = [
	SERVICE_STORAGE_TIMEFRAME,
	SERVICE_STORAGE_STATUS,
	SERVICE_STORAGE_DEPARTMENT,
	MODEL_STORAGE_STATUS,
	MODEL_STORAGE_DEPARTMENT,
	FEEDBACK_STORAGE_TIMEFRAME,
	FEEDBACK_STORAGE_STATUS,
	FEEDBACK_STORAGE_DEPARTMENT,
	DEFECTS_STORAGE_DEPARTMENT,
	DEFECTS_STORAGE_TIMEFRAME,
	DEFECTS_STORAGE_STATUS,
];

export const Months = [
	"Jan",
	"Feb",
	"Mar",
	"Apr",
	"May",
	"June",
	"July",
	"Aug",
	"Sep",
	"Oct",
	"Nov",
	"Dec",
];

//license type
export const LicenceType = (licenseLevel) => {
	switch (licenseLevel) {
		case "A":
			return getI18n().t("label.applicationLicense");
		case "S":
			return getI18n().t("label.siteLicense");
		case "C":
			return getI18n().t("label.clientLicense");
		default:
			return getI18n().t("label.unknownLicense");
	}
};

// file upload encoding notes
export const UPLOAD_CSV_NOTE =
	"NOTE: Please ensure that your CSV file is UTF-8 Encoded before uploading.";

export const DEFECT_NOTIFICATION_REFRESH_EVENT =
	"defect-notifications-refresh";

export const PRODUCT_UPDATE_NOTIFICATION_REFRESH_EVENT =
	"product-update-notifications-refresh";
